import React, { useMemo } from 'react';
import {
  InputTable,
  type InputTableCellParams,
  type InputTableRowParams,
  type RepeatRowsPayload
} from '@/components/custom/erp';
import { BankFeeRow, SelectedCellInfo } from '../hooks/useBankFeeRows';
import { copyCellValue } from '@/lib/table-handlers';
import { getBankFeeColumns } from './columns';
import { useBankFeePaste } from '../hooks';
import { FormMode } from '@/types/form';
import ActionBar from './ActionBar';

interface BankFeeTabProps {
  formMode: FormMode;
  rows: BankFeeRow[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: (params: InputTableRowParams<BankFeeRow>) => void;
  onCellClick?: (params: InputTableCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: unknown) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<BankFeeRow>) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<unknown[]>; resultField?: string }>
  ) => void;
  isPasting?: boolean;
}

export const BankFeeTab: React.FC<BankFeeTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  onRepeatForRows,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting
}) => {
  const columns = useMemo(() => getBankFeeColumns(onCellValueChange), [onCellValueChange]);

  useBankFeePaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  return (
    <InputTable
      rows={rows}
      autoHeight
      bottomSelector='[data-arito-bottom-bar]'
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={row => row?.uuid || ''}
      enableContextMenu
      onCopyData={({ columnValue, columnField, columnValues }) => {
        copyCellValue({
          columnField,
          columnValue,
          columnValues,
          fallback: text => console.log('Copy data per column:', text)
        });
      }}
      onRepeatForRows={onRepeatForRows}
      actionButtons={
        <ActionBar
          formMode={formMode}
          handleAddRow={onAddRow}
          handleDeleteRow={onDeleteRow}
          handleCopyRow={onCopyRow}
          handlePasteRow={onPasteRow}
          selectedCell={selectedCell}
          handleMoveRow={onMoveRow}
          handleExport={() => console.log('Export clicked')}
          handlePin={() => console.log('Pin clicked')}
          isPasting={isPasting}
        />
      }
    />
  );
};
